import { AuthContext } from "@/context/AuthContext";
import ConductionAPI, { BalancingResult } from "@/javascript/conduction-api";
import { Classroom, ProjectSubmissionToken } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import {
    Badge,
    Button,
    Flex,
    Group,
    Pagination,
    ScrollArea,
    Skeleton,
    Stack,
    Table,
    Text,
    Tooltip
} from "@mantine/core";
import { IconAlertCircle, IconCheck, IconCircleCheck, IconExclamationCircle, IconFilePlus, IconInfoSquare, IconScale, IconTrash } from "@tabler/icons-react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import ConductionModal from "../ConductionModal";
import AdminClassroomSubmissionTokenCreator from "./ClassroomSubmissionTokenCreationForm";

interface ClassroomSubmissionTokensBrowserProps {
    classroom: Classroom;
    onClose: () => void;
    onUpdated: () => void;
}

const ClassroomSubmissionTokensBrowser: React.FC<ClassroomSubmissionTokensBrowserProps> = ({ classroom, onClose, onUpdated }) => {
    const [submissionTokens, setSubmissionTokens] = useState<ProjectSubmissionToken[] | null>(null);
    const [desiredPage, setDesiredPage] = useState<number>(1);
    const [tokensPerPage, setTokensPerPage] = useState<number>(6);
    const [loading, setLoading] = useState<boolean>(true);
    const [balancingResults, setBalancingResults] = useState<BalancingResult[] | null>(null);

    const auth = useContext(AuthContext);
    if (auth === undefined) {
        throw new Error("ProjectSelect must be a child of AuthContext.");
    }
    const token = auth.token;
    if (token === null) {
        return;
    }

    const height = 60; // view height (vh)
    const calculateUsersPerPage = useCallback(() => {
        const rowHeight = 45; // pixel estimation
        const containerHeight = window.innerHeight * (height / 100) - 100; // Use 60% of the viewport height minus some estimate for other elements
        setTokensPerPage(Math.floor(containerHeight / rowHeight));

        if (submissionTokens && Math.floor(containerHeight / rowHeight) * (desiredPage - 1) + 1 > submissionTokens.length) {
            setDesiredPage(1);
        }
    }, [desiredPage, window.innerHeight, submissionTokens]);

    useEffect(() => {
        calculateUsersPerPage();
        window.addEventListener("resize", calculateUsersPerPage);
        return () => window.removeEventListener("resize", calculateUsersPerPage);
    }, [calculateUsersPerPage]);

    const deleteToken = (tokenId: string) => {
        const notificationId = Utils.displayNotification(
            "Deleting Token",
            undefined,
            "blue",
            "loading"
        );

        ConductionAPI.removeSubmissionToken(classroom.id, tokenId, token)
            .then(() => {
                setSubmissionTokens(
                    submissionTokens ? submissionTokens
                        .filter(st => st.id != tokenId)
                        .sort((a, b) =>
                            b.dateCreated.getTime() - a.dateCreated.getTime() // Newest first
                        ) : []
                );
                Utils.updateNotification(
                    notificationId,
                    "Credits Loaded",
                    undefined,
                    "green",
                    <IconCheck size={16} />
                );
            })
            .catch(() => {
                Utils.updateNotification(
                    notificationId,
                    "Error Occurred",
                    undefined,
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            });
    }

    const fetchSubmissionTokens = () => {
        const notificationId = Utils.displayNotification(
            "Loading Credits",
            undefined,
            "blue",
            "loading"
        );

        ConductionAPI.getSubmissionTokens(classroom.id, token)
            .then((loadedSubmissionCredits: ProjectSubmissionToken[] | null) => {
                if (!loadedSubmissionCredits || loadedSubmissionCredits.length === 0) {
                    setSubmissionTokens(null);
                    Utils.updateNotification(
                        notificationId,
                        "No Credits Found",
                        undefined,
                        "red",
                        <IconAlertCircle size={16} />,
                        true
                    );
                } else {
                    setSubmissionTokens(
                        loadedSubmissionCredits
                            .sort((a, b) =>
                                b.dateCreated.getTime() - a.dateCreated.getTime() // Newest first
                            )
                    );
                    Utils.updateNotification(
                        notificationId,
                        "Credits Loaded",
                        undefined,
                        "green",
                        <IconCheck size={16} />
                    );
                }
            })
            .catch(() => {
                Utils.updateNotification(
                    notificationId,
                    "Error Fetching Credits",
                    undefined,
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            })
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        fetchSubmissionTokens();
    }, []);

    const handleBalanceSubmissionCredits = () => {
        setLoading(true);
        const notificationId = Utils.displayNotification(
            "Attempting Balance...",
            undefined,
            "blue",
            "loading"
        );

        ConductionAPI.balanceSubmissionTokens(classroom.id, token)
            .then((results: BalancingResult[]) => {
                setBalancingResults(results)
                Utils.updateNotification(
                    notificationId,
                    "Balancing Complete!",
                    "See the results below.",
                    "blue",
                    <IconAlertCircle size={16} />,
                    true
                );
            })
            .catch(() => {
                Utils.updateNotification(
                    notificationId,
                    "An Error Occurred",
                    undefined,
                    "red",
                    <IconAlertCircle size={16} />,
                    true
                );
            });
    }

    const paginatedTokens = useMemo(() => {
        const start = (desiredPage - 1) * tokensPerPage;
        return submissionTokens?.slice(start, start + tokensPerPage) ?? [];
    }, [desiredPage, submissionTokens, tokensPerPage]);

    const activeTokens = useMemo(() => submissionTokens?.filter((currentToken) => currentToken.isActive()) ?? [], [submissionTokens]);
    const totalActiveCredits = useMemo(() => activeTokens.reduce((acc: number, token: ProjectSubmissionToken) => acc + token.credits, 0), [activeTokens]);
    const usedActiveCredits = useMemo(() => activeTokens.reduce((acc: number, token: ProjectSubmissionToken) => acc + token.submissions.length, 0), [activeTokens]);
    const remainingActiveCredits = useMemo(() => totalActiveCredits - usedActiveCredits, [totalActiveCredits, usedActiveCredits]);

    const [showClassroomSubmissionTokenCreationModal, setShowClassroomSubmissionTokenCreationModal] = useState<boolean>(false);
    const renderModals = () => (
        <>
            {showClassroomSubmissionTokenCreationModal && (
                <AdminClassroomSubmissionTokenCreator
                    classroomId={classroom.id}
                    onClose={() => setShowClassroomSubmissionTokenCreationModal(false)}
                    onCreated={() => {
                        fetchSubmissionTokens();
                        onUpdated();
                    }}
                />
            )}

            {balancingResults != null && (
                <ConductionModal
                    onClose={() => {
                        setBalancingResults(null);
                        fetchSubmissionTokens();
                    }}
                    title="Balancing Results"
                    size="80vw"
                    h={"80vh"}
                >
                    <Stack>
                        {(balancingResults == null || balancingResults.length <= 0)
                            ?
                            <Text>
                                No results.
                            </Text>
                            :
                            <Stack>
                                {balancingResults.map((balancingResult) => (
                                    <Group>
                                        {(balancingResult.success === null) ?
                                            <IconCircleCheck size={24} color="blue" />
                                            : (balancingResult.success === true) ?
                                                <IconCircleCheck size={24} color="green" />
                                                :
                                                <IconExclamationCircle size={24} color="red" />
                                        }
                                        <Text>
                                            {balancingResult.message}
                                        </Text>
                                    </Group>
                                ))}
                            </Stack>
                        }
                        {balancingResults !== null && balancingResults.some(br => br.success === false) &&
                            <Group>
                                <IconInfoSquare size={24} color="blue" />
                                <Text>
                                    Consider adding credits that fit the time frames above to remedy the discrepency.
                                </Text>
                            </Group>
                        }
                    </Stack>
                </ConductionModal>
            )}
        </>
    );

    return (
        <ConductionModal
            onClose={onClose}
            title="Classroom Submission Credits"
            size="lg"
        >
            <Stack align="center" justify="center">
                {renderModals()}
                <Group>
                    <Button
                        onClick={() => setShowClassroomSubmissionTokenCreationModal(true)}
                    >
                        <IconFilePlus size={16} style={{ marginRight: 8 }} />
                        Add Submission Credits
                    </Button>
                    <Tooltip
                        withArrow
                        label="Attempt to associate orphaned submissions with these tokens' credits"
                    >
                        <Button
                            onClick={handleBalanceSubmissionCredits}
                            disabled={submissionTokens == null || submissionTokens.length === 0}
                        >
                            <IconScale size={16} style={{ marginRight: 8 }} />
                            Balance Credits
                        </Button>
                    </Tooltip>
                </Group>

                <Flex
                    direction={"column"}
                    align="center"
                    justify="space-between"
                    style={{
                        height: `${height}vh`,
                        width: "100%"
                    }}
                >
                    {loading ? (
                        <Skeleton height={"100%"} radius="md" />
                    ) : submissionTokens === null || submissionTokens.length === 0 ? (
                        <Text>No tokens found.</Text>
                    ) : (
                        <ScrollArea
                            style={{
                                width: "100%"
                            }}
                        >
                            <Table
                                striped
                                stickyHeader
                                highlightOnHover
                            >
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>Credits Available</Table.Th>
                                        <Table.Th>Used Credits</Table.Th>
                                        <Table.Th>Created Date</Table.Th>
                                        <Table.Th>Active Start Date</Table.Th>
                                        <Table.Th>Expiration Date</Table.Th>
                                        <Table.Th>Actions</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {paginatedTokens.map((submissionToken) => {
                                        return (
                                            <Table.Tr key={submissionToken.id}>
                                                <Table.Td>{submissionToken.credits}</Table.Td>
                                                <Table.Td><Badge color={submissionToken.submissions.length >= submissionToken.credits ? "red" : "blue"}>{submissionToken.submissions.length}</Badge></Table.Td>
                                                <Table.Td><Badge color="blue">{formatDateString(submissionToken.dateCreated)}</Badge></Table.Td>
                                                <Table.Td><Badge color={submissionToken.passedStartDate() ? "green" : "yellow"}>{formatDateString(submissionToken.startDate)}</Badge></Table.Td>
                                                <Table.Td><Badge color={submissionToken.isExpired() ? "red" : "green"}>{formatDateString(submissionToken.expirationDate)}</Badge></Table.Td>
                                                <Table.Td>
                                                    <Tooltip
                                                        label={"Delete"}
                                                        withArrow
                                                    >
                                                        <Button
                                                            size="xs"
                                                            onClick={async () => deleteToken(submissionToken.id)}
                                                        >
                                                            <IconTrash size={20} />
                                                        </Button>
                                                    </Tooltip>
                                                </Table.Td>
                                            </Table.Tr>
                                        );
                                    })}
                                </Table.Tbody>
                            </Table>
                        </ScrollArea>
                    )}

                    {submissionTokens && submissionTokens.length > tokensPerPage && (
                        <Pagination
                            total={Math.ceil(submissionTokens.length / tokensPerPage)}
                            value={desiredPage}
                            onChange={setDesiredPage}
                        />
                    )}
                    <ScrollArea
                        style={{
                            width: "100%"
                        }}
                    >
                        <Table
                            striped
                            stickyHeader
                            highlightOnHover
                        >
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Total Available Active Credits</Table.Th>
                                    <Table.Th>Total Used Active Credits</Table.Th>
                                    <Table.Th>Total Remaining Active Credits</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                <Table.Tr>
                                    <Table.Td><Badge color={totalActiveCredits > 0 ? "green" : "red"}>{totalActiveCredits}</Badge></Table.Td>
                                    <Table.Td><Badge color={usedActiveCredits >= totalActiveCredits ? "red" : "green"}>{usedActiveCredits}</Badge></Table.Td>
                                    <Table.Td><Badge color={remainingActiveCredits > 0 ? "green" : "red"}>{remainingActiveCredits}</Badge></Table.Td>
                                </Table.Tr>
                            </Table.Tbody>
                        </Table>
                    </ScrollArea>
                </Flex>
            </Stack>
        </ConductionModal>
    );
};

function formatDateString(stringDate: Date): string {
    if (!stringDate) return "Invalid Date";
    const date = new Date(stringDate + " UTC");
    return date.toLocaleDateString();
}

export default ClassroomSubmissionTokensBrowser;
